import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { DatePipe } from '@angular/common';
import { TranslationService } from '@/layout/service/translation.service';
import { RoomUsageState } from '@/cloud/models/room-usage.models';
import { bookingProgress } from 'src/api/room-usage';
import { RoomUsageSummaryComponent } from './room-usage-summary.component';
import { RoomEnvironmentComponent } from './room-environment.component';
import { translateCount } from '@/layout/service/translate-count';

/**
 * Room Overview usage card body: the shared usage summary (status pill: In a call, In a meeting,
 * amber Booked but vacant, ...), the room's capacity (or "Capacity not set" with a Set capacity
 * action for managers), the live temperature / humidity / light, the current meeting (organizer, times, progress), the next meeting and
 * when the state was last updated.
 */
@Component({
    selector: 'app-room-usage-card',
    standalone: true,
    imports: [DatePipe, RoomUsageSummaryComponent, RoomEnvironmentComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <app-room-usage-summary [usage]="usage()" [stale]="stale()" [capacity]="capacity()" />

        <div class="usage-capacity" [class.usage-capacity--unset]="capacity() === null">
            <i class="fa fa-person" aria-hidden="true"></i>
            <span>{{ capacity() === null ? t()('roomCapacity.notSet') : seats() }}</span>
            @if (canManage() && capacity() === null) {
                <button type="button" class="usage-capacity__cta" (click)="editProfile.emit()">{{ t()('roomCapacity.set') }}</button>
            }
            @if (usage().hasConferencing && !usage().inCall) {
                <span class="usage-capacity__call"><i class="fa fa-video-slash" aria-hidden="true"></i> {{ t()('roomCall.notInCall') }}</span>
            }
        </div>

        <app-room-environment [usage]="usage()" [stale]="stale()" />

        @if (usage().current; as current) {
            <div class="usage-meeting usage-meeting--current" [class.usage-meeting--stale]="stale()">
                <span class="usage-meeting__label">{{ t()('roomUsage.currentMeeting') }}</span>
                <span class="usage-meeting__title">
                    @if (current.isPrivate) {
                        <i class="fa fa-lock" aria-hidden="true"></i>
                    }
                    {{ current.title }}
                </span>
                <span class="usage-meeting__meta">
                    {{ current.startUtc | date: 'shortTime' }} – {{ current.endUtc | date: 'shortTime' }}
                    @if (current.organizer) {
                        · {{ current.organizer }}
                    }
                </span>
                <div class="usage-progress" role="progressbar" [attr.aria-label]="t()('roomUsage.meetingProgress')" aria-valuemin="0" aria-valuemax="100" [attr.aria-valuenow]="progress()">
                    <span class="usage-progress__bar" [style.width.%]="progress()"></span>
                </div>
            </div>
        }

        @if (usage().next; as next) {
            <div class="usage-meeting" [class.usage-meeting--stale]="stale()">
                <span class="usage-meeting__label">{{ t()('roomUsage.nextMeeting') }}</span>
                <span class="usage-meeting__title">
                    @if (next.isPrivate) {
                        <i class="fa fa-lock" aria-hidden="true"></i>
                    }
                    {{ next.title }}
                </span>
                <span class="usage-meeting__meta">
                    {{ next.startUtc | date: 'shortTime' }} – {{ next.endUtc | date: 'shortTime' }}
                    @if (next.organizer) {
                        · {{ next.organizer }}
                    }
                </span>
            </div>
        } @else if (usage().hasBooking && !usage().current) {
            <p class="usage-muted">{{ t()('roomUsage.noMoreMeetings') }}</p>
        }

        @if (!stale()) {
            <p class="usage-muted usage-updated">{{ t()('roomUsage.updated') }} {{ usage().updatedUtc | date: 'shortTime' }}</p>
        }
    `,
    styles: [
        `
            :host {
                display: flex;
                flex-direction: column;
                gap: var(--app-space-3);
                min-width: 0;
                overflow-wrap: anywhere;
            }
            .usage-meeting {
                display: flex;
                flex-direction: column;
                gap: var(--app-space-1);
                padding-top: var(--app-space-3);
                border-top: 1px solid var(--surface-border);
            }
            .usage-meeting--stale {
                opacity: 0.7;
            }
            .usage-meeting__label {
                font-size: 0.75rem;
                font-weight: 600;
                letter-spacing: 0.02em;
                text-transform: uppercase;
                color: var(--text-color-secondary);
            }
            .usage-meeting__title {
                font-weight: 600;
            }
            .usage-meeting__meta,
            .usage-muted {
                color: var(--text-color-secondary);
                font-size: 0.8125rem;
            }
            .usage-muted {
                margin: 0;
            }
            .usage-progress {
                height: 0.375rem;
                margin-top: var(--app-space-1);
                border-radius: 999px;
                background: var(--app-surface-inset-strong, var(--surface-border));
                overflow: hidden;
            }
            .usage-progress__bar {
                display: block;
                height: 100%;
                border-radius: inherit;
                background: var(--primary-color);
            }
            .usage-capacity {
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                gap: 0.4rem 0.75rem;
                color: var(--text-color-secondary);
                font-size: 0.8125rem;
            }
            .usage-capacity--unset > span:first-of-type {
                font-style: italic;
            }
            .usage-capacity__cta {
                padding: 0;
                border: 0;
                background: none;
                color: var(--primary-color);
                font: inherit;
                font-weight: 500;
                cursor: pointer;
            }
            .usage-capacity__cta:hover {
                text-decoration: underline;
            }
            .usage-capacity__cta:focus-visible {
                outline: 2px solid var(--primary-color);
                outline-offset: 2px;
            }
            .usage-capacity__call {
                display: inline-flex;
                align-items: center;
                gap: 0.35rem;
            }
            .usage-updated {
                margin-top: auto;
            }
        `
    ]
})
export class RoomUsageCardComponent {
    readonly t = inject(TranslationService).t;

    readonly usage = input.required<RoomUsageState>();
    readonly stale = input(false);
    /** Clock (epoch ms) so the progress bar advances; pass a ticking signal from the parent. */
    readonly now = input<number>(Date.now());
    /** Effective capacity (cloud-set, else sensor-reported); null when unknown. */
    readonly capacity = input<number | null>(null);
    /** The viewer may set the room's capacity (locations.manage). */
    readonly canManage = input(false);
    readonly editProfile = output<void>();

    readonly seats = computed(() => translateCount(this.t(), 'roomCapacity.seats', this.capacity()));

    readonly progress = computed(() => {
        const current = this.usage().current;
        return current ? Math.round(bookingProgress(current, this.now()) * 100) : 0;
    });
}
